// src/pages/Services.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import servicesImage from '../assets/services.png';

function Services() {
  const [hoveredService, setHoveredService] = useState(null);

  const servicesList = [
    { 
      name: "Financial Management Services", 
      desc: "Comprehensive financial oversight and reporting to align your daily financial operations with long-term strategic business goals." 
    },
    { 
      name: "Financial Advisory", 
      desc: "Expert guidance on capital structuring, risk management, and strategic investments to drive profitability and operational efficiency." 
    },
    { 
      name: "Transaction Advisory (M&A)", 
      desc: "End-to-end support for mergers, acquisitions, and divestitures, including rigorous financial valuation and due diligence." 
    },
    { 
      name: "Capital Raising & Investor Relations", 
      desc: "Connecting your business with the right investors, managing fundraising cycles, and ensuring transparent ongoing communications." 
    },
    { 
      name: "Advanced Cash Flow Management", 
      desc: "Optimizing enterprise liquidity through predictive modeling, working capital adjustments, and stringent expense controls." 
    },
    { 
      name: "Growth-Centric Financial Strategy & Modeling", 
      desc: "Building robust, dynamic financial models and projections to chart a clear, scalable path for organic and inorganic expansion." 
    },
    { 
      name: "Technology Integration & System Architecture", 
      desc: "Implementing and optimizing automated ERP workflows and modern financial tech stacks for maximum operational efficiency." 
    },
    { 
      name: "Risk Management, Governance, & Restructuring", 
      desc: "Identifying structural vulnerabilities, ensuring regulatory compliance, and executing restructuring operations for resilient corporate health." 
    },
    { 
      name: "Accounting & Bookkeeping Services", 
      desc: "Accurate, timely, and fully compliant financial record-keeping tailored specifically to complex, high-growth enterprise requirements." 
    },
    { 
      name: "Tax Advisory & Compliance", 
      desc: "Strategic global and domestic tax planning combined with strict regulatory compliance to minimize liabilities and maximize returns." 
    }
  ];

  // Distinct vibrant colors for the smart borders
  const cardColors = [
    '#E1261C', // NAPREV Red
    '#00537A', // NAPREV Blue
    '#F59E0B', // Amber
    '#10B981', // Emerald
    '#8B5CF6', // Purple
    '#EC4899', // Pink
    '#3B82F6', // Light Blue
    '#14B8A6', // Teal
    '#F97316', // Orange
    '#6366F1'  // Indigo
  ];

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', color: '#333' }}>
      
      {/* 1. Overview Section */}
      <section style={styles.overviewSection}>
        <div style={styles.overviewContainer}>
          <div style={styles.overviewTextContainer}>
            <h1 style={styles.overviewTitle}>Our Comprehensive Services</h1>
            <p style={styles.overviewDesc}>
              NAPREV delivers a robust suite of financial advisory and accounting services designed to empower your business. 
              From day-to-day bookkeeping to complex M&A transaction advisory, we provide the strategic insight and operational 
              muscle needed to drive scalable growth. Partner with our elite team of CAs, CFAs, and MBAs to transform your 
              financial workflows and establish a sustainable competitive advantage.
            </p>
          </div>
          <div style={styles.overviewImageContainer}>
            <img 
              src={servicesImage} 
              alt="NAPREV Services Overview" 
              style={styles.overviewImage} 
            />
          </div>
        </div>
      </section>

      {/* 2 & 3. Services Grid Section */}
      <section style={styles.gridSection}>
        <div style={styles.gridContainer}>
          {servicesList.map((service, index) => {
            const slug = service.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const isHovered = hoveredService === index;
            const cardColor = cardColors[index % cardColors.length];
            
            return (
              <div 
                key={index} 
                style={{
                  ...styles.serviceCard,
                  borderTop: `5px solid ${cardColor}`,
                  ...(isHovered ? {
                    transform: 'translateY(-10px)',
                    boxShadow: `0 20px 25px -5px ${cardColor}40`, // dynamic tinted shadow
                  } : {})
                }}
                onMouseEnter={() => setHoveredService(index)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <div style={styles.cardNumber}>{index + 1 < 10 ? `0${index + 1}` : index + 1}</div>
                <h3 style={styles.serviceTitle}>{service.name}</h3>
                <p style={styles.serviceDesc}>{service.desc}</p>
                <Link 
                  to={`/services/${slug}`} 
                  style={{
                    ...styles.knowMoreBtn,
                    ...(isHovered ? { backgroundColor: cardColor, color: '#FFFFFF' } : {})
                  }}
                >
                  Know More <span style={{ ...styles.arrow, ...(isHovered ? styles.arrowHover : {}) }}>&rarr;</span>
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA Section */}
      <section style={styles.ctaSection}>
        <h2 style={{ fontSize: '28px', marginBottom: '15px' }}>Need a tailored financial solution?</h2>
        <Link to="/contact" style={styles.secondaryButton}>Contact Our Team</Link>
      </section>
    </div>
  );
}

const styles = {
  overviewSection: { 
    padding: '40px 5% 50px', // Reduced height so the grid below peeks into view
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E5E7EB'
  },
  overviewContainer: {
    maxWidth: '1100px', // Slightly narrowed width for sleekness
    margin: '0 auto',
    display: 'flex', 
    flexWrap: 'wrap', 
    alignItems: 'center', 
    gap: '40px',
  },
  overviewTextContainer: { 
    flex: 1, 
    minWidth: '300px' 
  },
  overviewTitle: { 
    fontSize: '36px', // Scaled down slightly to fit better vertically
    color: '#002B49', 
    marginBottom: '15px', 
    fontWeight: 'bold' 
  },
  overviewDesc: { 
    fontSize: '17px', 
    lineHeight: '1.6', 
    color: '#4B5563',
    margin: 0,
    textAlign: 'justify'
  },
  overviewImageContainer: { 
    flex: 1, 
    minWidth: '300px' 
  },
  overviewImage: { 
    width: '100%', 
    height: 'auto', 
    maxHeight: '300px', // Reduced height for the peek effect
    objectFit: 'cover',
    borderRadius: '12px', 
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
  },
  
  gridSection: { 
    padding: '60px 5% 100px',
    backgroundColor: '#F4F7F9'
  },
  gridContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '30px'
  },
  serviceCard: { 
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '40px 30px',
    borderLeft: '1px solid #E5E7EB',
    borderRight: '1px solid #E5E7EB',
    borderBottom: '1px solid #E5E7EB',
    boxShadow: '0 4px 6px rgba(0,0,0,0.03)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden'
  },
  cardNumber: {
    fontSize: '64px',
    fontWeight: '900',
    color: '#F3F4F6',
    position: 'absolute',
    top: '10px',
    right: '20px',
    lineHeight: '1',
    zIndex: 0,
    transition: 'color 0.3s ease'
  },
  serviceTitle: { 
    fontSize: '22px', 
    color: '#002B49', 
    fontWeight: 'bold',
    margin: '0 0 15px 0',
    zIndex: 1
  },
  serviceDesc: { 
    fontSize: '16px', 
    color: '#4B5563', 
    lineHeight: '1.6', 
    margin: '0 0 30px 0',
    flexGrow: 1,
    zIndex: 1
  },
  knowMoreBtn: { 
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#002B49', 
    textDecoration: 'none', 
    fontWeight: 'bold',
    fontSize: '15px',
    padding: '12px 24px',
    backgroundColor: '#F3F4F6',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    alignSelf: 'flex-start',
    zIndex: 1
  },
  arrow: {
    fontSize: '18px',
    transition: 'transform 0.3s ease'
  },
  arrowHover: {
    transform: 'translateX(4px)'
  },

  ctaSection: { 
    backgroundColor: '#002B49', 
    color: '#fff', 
    padding: '50px 5%', 
    textAlign: 'center' 
  },
  secondaryButton: { 
    display: 'inline-block',
    backgroundColor: '#E1261C', 
    color: '#fff', 
    padding: '15px 35px', 
    textDecoration: 'none', 
    fontSize: '18px', 
    fontWeight: 'bold', 
    borderRadius: '6px',
    marginTop: '10px',
    transition: 'opacity 0.3s ease'
  }
};

export default Services;